// Configuración por país (ISO alpha-2) del formulario de datos bancarios del
// vendedor en Mi Cuenta: etiqueta y validación del documento del titular,
// tipos de cuenta y validación del número de cuenta.
//
// Los tipos de cuenta se guardan como ETIQUETA (no slug) a propósito: es lo que
// ya contiene `bankData.accountType` para los repartidores registrados por el
// widget, y lo que muestran las vistas de lectura (modal "Ver" del admin).
import {
  isValidRut,
  isValidCuit,
} from "../../adminView/settings/sections/invoicing/fiscalValidation";

// Mismo centinela que el selector de bancos del operador: la nómina por país
// puede quedar desactualizada y no debe bloquear el guardado.
export const OTHER_BANK = "__other__";

export const COUNTRY_FORM = {
  CL: {
    docLabel: "RUT del titular",
    docPlaceholder: "12.345.678-5",
    docValidate: isValidRut,
    docError: "RUT inválido (revisa el dígito verificador)",
    accountTypes: [
      "Cuenta Corriente",
      "Cuenta Vista",
      "Cuenta de Ahorro",
      "Cuenta RUT",
    ],
    accountPlaceholder: "001234567890",
    accountValidate: (v) => /^[0-9-]{4,20}$/.test(v),
    accountError: "Número de cuenta inválido (solo dígitos y guiones)",
  },
  AR: {
    docLabel: "DNI o CUIT del titular",
    docPlaceholder: "12345678 ó 20-12345678-3",
    // DNI de 7-8 dígitos, o CUIT/CUIL con dígito verificador (módulo 11).
    docValidate: (v) =>
      /^\d{7,8}$/.test(String(v).replace(/\D/g, "")) || isValidCuit(v),
    docError: "Documento inválido (DNI de 7-8 dígitos o CUIT/CUIL)",
    accountTypes: ["Cuenta Corriente", "Caja de Ahorro"],
    accountPlaceholder: "CBU/CVU (22 dígitos) o alias",
    // CBU/CVU exactos de 22 dígitos, o alias (6-20, letras/números/punto/guion).
    accountValidate: (v) =>
      /^\d{22}$/.test(String(v).replace(/\s/g, "")) ||
      /^[a-zA-Z0-9.-]{6,20}$/.test(String(v).trim()),
    accountError: "Ingresa un CBU/CVU de 22 dígitos o un alias válido",
  },
  CO: {
    docLabel: "Cédula del titular",
    docPlaceholder: "1234567890",
    docValidate: (v) => /^\d{6,10}$/.test(String(v).replace(/\D/g, "")),
    docError: "Cédula inválida (6 a 10 dígitos)",
    accountTypes: ["Cuenta Corriente", "Cuenta de Ahorros"],
    accountPlaceholder: "Número de cuenta",
    accountValidate: (v) => /^[0-9-]{6,20}$/.test(v),
    accountError: "Número de cuenta inválido (solo dígitos y guiones)",
  },
  PE: {
    docLabel: "DNI del titular",
    docPlaceholder: "12345678",
    docValidate: (v) => /^\d{8,9}$/.test(String(v).replace(/\D/g, "")),
    docError: "DNI inválido (8 dígitos)",
    accountTypes: ["Cuenta Corriente", "Cuenta de Ahorros"],
    accountPlaceholder: "Número de cuenta o CCI",
    accountValidate: (v) => /^[0-9-]{8,22}$/.test(v),
    accountError: "Número de cuenta inválido (solo dígitos y guiones)",
  },
};

// Fallback para países sin configuración específica: etiquetas genéricas y
// validación laxa — mejor aceptar un dato revisable que bloquear el guardado.
export const DEFAULT_FORM = {
  docLabel: "Documento del titular",
  docPlaceholder: "Documento de identidad",
  docValidate: (v) => String(v).trim().length >= 5,
  docError: "Documento inválido",
  accountTypes: ["Cuenta Corriente", "Cuenta de Ahorros"],
  accountPlaceholder: "Número de cuenta",
  accountValidate: (v) => String(v).trim().length >= 4,
  accountError: "Número de cuenta inválido",
};

export const formConfigForCountry = (iso) => COUNTRY_FORM[iso] || DEFAULT_FORM;

// Estado inicial del formulario a partir del `bankData` guardado. Un banco que
// no está en la nómina del país (guardado a mano, o nómina desactualizada) cae
// en "Otro banco" con su nombre preservado como texto libre.
export function initialForm(bankData, bankOptions) {
  const bd = bankData || {};
  const inCatalog = bd.bank && bankOptions?.includes(bd.bank);
  return {
    name: bd.name || "",
    rut: bd.rut || "",
    bank: inCatalog ? bd.bank : bd.bank ? OTHER_BANK : "",
    bankOther: inCatalog ? "" : bd.bank || "",
    accountType: bd.accountType || "",
    accountNumber: bd.accountNumber || "",
  };
}
